import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Viewer } from '@photo-sphere-viewer/core';
import { MarkersPlugin } from '@photo-sphere-viewer/markers-plugin';
import '@photo-sphere-viewer/core/index.css';
import '@photo-sphere-viewer/markers-plugin/index.css';
import { useTranslation } from 'react-i18next';
import { Button, CardContent, Modal } from '@mui/material';
import MainCard from 'components/MainCard';
import { equipmentFaultStatusColors, equipmentISOStatus, getEquipmentStatusColorByStatus, markerColorByStatus } from 'data/data';
import { getEquipmentLocationMapByMapId } from 'components/axios/equipmentZoneAxios';
import { useEnumApi } from 'contexts/EnumApiContext';
import { getEquipmentsById } from 'components/axios/equipmentAxios';

export function CompanyPanoViewerLayer({fileUrl, mapsId}) {
  const { getEnumValueByEnumNameAndCode } = useEnumApi();
  const { t } = useTranslation();

  const [open, setOpen] = useState(false);

  const handleOpen = () => {
    setOpen(true);
  }
  const handleClose = () => setOpen(false);
  
  const containerRef = useRef(null);
  const viewerRef = useRef(null);
  const [markers, setMarkers] = useState([]); // marker infos(id, position)
  const [equipmentMappingList, setEquipmentMappingList] = useState(null);

  useEffect(()=>{
    if (open) {
      getEquipmentLocationMapByMapId(mapsId)
      .then(async (res) => {
        if (!res || !res.coordinates) {
          setEquipmentMappingList(null);
          setMarkers([]);
          return;
        }

        // 1. 기본 좌표 정보 및 mId 생성
        const mappingList = res.coordinates.map((m) => ({
          ...m,
          mId: `marker-${Math.floor(Math.random() * 10000)}`
        }));

        // 2. 각 설비의 상세 정보(getEquipmentById) 병렬 호출
        const updatedMarkers = await Promise.all(
          mappingList.map(async (m) => {
            let fullEquipment = m.equipment;

            // 설비 ID가 존재하는 경우 상세 정보 API 호출
            if (m.equipment?.id) {
              try {
                const detail = await getEquipmentsById(m.equipment.id);
                fullEquipment = { ...m.equipment, ...detail };
              } catch (err) {
                console.error(`Equipment ID ${m.equipment.id} 조회 실패`, err);
              }
            }

            return {
              ...m,
              mId: m.mId,
              equipment: fullEquipment,
            };
          })
        );

        // 3. 완성된 마커 데이터 저장
        // console.log("상세 정보가 포함된 마커 목록:", updatedMarkers);
        setEquipmentMappingList(updatedMarkers);
      })
      .catch((err) => console.error("Map Data Loading error:", err));
      
      // getEquipmentLocationMapByMapId(mapsId).then((res) => {
      //   // console.log(res)
      //   let list = [];
      //   if (res) {
      //     let mappingList = [];
      //     res.coordinates?.forEach((m)=> {
      //       let markerId = `marker-${Math.floor(Math.random() * 10000)}`;
      //       mappingList.push({...m, mId : markerId})
      //     })
      //     console.log(mappingList)
      //     setEquipmentMappingList(mappingList);
      //   } else {
      //     setEquipmentMappingList(null)
      //   }
      // })
    }
  }, [open])
  
  useLayoutEffect(() => {
    // console.log(1)
    if (!open || !equipmentMappingList) return; // DOM 준비되지 않았으면 초기화하지 않음
    const timer = setTimeout(() => {
      if (!containerRef.current) return; // DOM 준비되지 않았으면 초기화하지 않음
      // console.log(2)
      const viewer = new Viewer({
        container: containerRef.current,
        // panorama: `${baseUrl}sphere.jpg`,
        panorama: fileUrl,
        plugins: [
          [MarkersPlugin, {}]
        ],
      });

      viewer.addEventListener('ready', async () => {
        const markersPlugin = viewer.getPlugin(MarkersPlugin);
        // markersPluginRef.current = markersPlugin;

        // 서버에서 저장된 마커 데이터 가져오기
        // console.log(equipmentMappingList)
        const markers = equipmentMappingList?.map((m) => {return { id:m.mId, equipment: m.equipment, position: { yaw: m.yawOrX, pitch: m.pitchOrY } }});
        // console.log(markers)
        setMarkers(markers);
        markers?.forEach((m) => {
          const statusColor = getEquipmentStatusColorByStatus(m.equipment?.status);
          const isoStatus = equipmentISOStatus.find((iso) => iso.code === m.equipment?.lowestDiagnosisRank);
          const isoColor = isoStatus?.color3 || '#62b929';
          const isoCode = (isoStatus || { code: '-' }).code;
          // console.log(m)
          markersPlugin.addMarker({
            id: m.id,
            position: m.position, // {yaw, pitch}
            image: markerColorByStatus[m.equipment?.status || "unmapped"],
            size: { width: 32, height: 32 },
            anchor: "bottom center",
            tooltip: {
              content : `
              <div class="map_pin"
                style="width: 320px;
                  height: 150px;
                  padding: 20px;
                  background: unset;"
              >
                <div style="width:300px;
                  padding: 12px 16px;
                  border-radius: 10px;
                  color: #fff;
                  font-size: 14px;
                  display: flex;
                  flex-direction: column;
                  border: 2px solid rgba(0, 0, 0, 0.25);
                  gap: 6px;">
                  
                  <!-- 상단 제목 -->
                  <div style="
                    box-sizing: border-box !important;
                    border-radius: 12px !important;
                    width: 100% !important;
                    background: ${statusColor?.bg2 ?? "#dddddd"} !important;
                    padding: 8px 10px !important;
                    text-align: center !important;
                    font-weight: bold !important;
                    font-size: 13px !important;
                    color: ${statusColor?.color ?? '#fff'} !important;
                    word-break: break-all !important;
                    box-shadow: 0 2px 4px rgba(0,0,0,0.2) !important;
                  ">
                    ${m.equipment ? `${m.equipment.name} (${m.equipment.equipmentNo})` : t('message.common.text.Unmapped')}
                  </div>

                  <!-- 항목 목록 -->
                  <div style="display: flex !important; flex-direction: column !important; gap: 8px !important; margin-top: 2px !important;">
                    
                    <!-- 설비 분류 -->
                    <div style="display: flex !important; justify-content: space-between !important; align-items: center !important;">
                      <span style="color: #fff !important; font-size: 12px !important;">${t("message.common.subtitle.equipment.level3")}</span>
                      <span style="font-weight: 600 !important; color: #fff !important;">${m.equipment?.equipmentCtgr?.name ?? "-"}</span>
                    </div>

                    <!-- 상태 -->
                    <div style="display: flex !important; justify-content: space-between !important; align-items: center !important;">
                      <span style="color: #fff !important; font-size: 12px !important;">${t("message.common.subtitle.status")}</span>
                      <span style="
                        display: inline-flex !important;
                        align-items: center !important;
                        justify-content: center !important;
                        background: ${statusColor?.bg2 ?? "#dddddd"} !important;
                        color: ${statusColor?.color ?? '#fff'} !important;
                        padding: 2px 10px !important;
                        border-radius: 10px !important;
                        font-size: 11px !important;
                        font-weight: bold !important;
                        line-height: 1 !important;
                      ">
                        ${getEnumValueByEnumNameAndCode("EquipmentFaultStatus", m.equipment?.status)?.[0]?.title ?? "-"}
                      </span>
                    </div>

                    <!-- 운행 정보 -->
                    <div style="display: flex !important; justify-content: space-between !important; align-items: center !important;">
                      <span style="color: #fff !important; font-size: 12px !important;">${t("message.common.subtitle.operation.info")}</span>
                      <span style="
                        display: inline-flex !important;
                        align-items: center !important;
                        justify-content: center !important;
                        background: ${m.equipment?.latestEquipmentOp?.opStatus?.toUpperCase() === "ACTIVE" ? "#2672f7" : "#777"} !important;
                        color: #fff !important;
                        padding: 2px 10px !important;
                        border-radius: 10px !important;
                        font-size: 11px !important;
                        line-height: 1 !important;
                      ">
                        ${m.equipment?.latestEquipmentOp?.opStatus?.toUpperCase() === "ACTIVE" ? t('message.common.text.active') : t('message.common.text.stop')}
                      </span>
                    </div>

                    <!-- GB/ISO 등급 -->
                    <div style="display: flex !important; justify-content: space-between !important; align-items: center !important;">
                      <span style="color: #fff !important; font-size: 12px !important;">${t('message.common.subtitle.iso')} ${t("message.common.subtitle.grade")}</span>
                      <span style="
                        display: inline-flex !important;
                        align-items: center !important;
                        justify-content: center !important;
                        background: ${isoColor} !important;
                        color: ${m.equipment?.lowestDiagnosisRank === "C" ? "#000":"#fff"} !important;
                        width: 20px !important;
                        height: 20px !important;
                        border-radius: 50% !important;
                        font-size: 11px !important;
                        font-weight: bold !important;
                        line-height: 1 !important;
                      ">
                        ${isoCode}
                      </span>
                    </div>

                  </div>
                </div>
              </div>
              `,
              position: 'top center',
            }
          });
        });

        // ⭐ 모든 마커를 추가한 후 첫 번째 마커 위치로 이동
        const firstMarker = markers?.[0];

        if (firstMarker?.position) {
          viewer.rotate({
            yaw: firstMarker.position.yaw,
            pitch: firstMarker.position.pitch,
            // speed: 800,
          });
        }
      });

      viewerRef.current = viewer;
    }, 0);

    return () => {
      clearTimeout(timer);
      if (viewerRef.current) {
        viewerRef.current.destroy();
      }
    };
  }, [open, equipmentMappingList]);

  // useEffect(() => {
  //   if (!viewerRef.current || !markers || markers.length === 0) return;
  //   const viewer = viewerRef.current;

  //   // // 기존 마커 추가
  //   // markers.forEach(marker => {
  //   //   markersPlugin.addMarker(marker);
  //   // });

  //   // 첫 번째 마커를 기준으로 카메라 이동
  //   const firstMarker = markers[0];
  //   // console.log(firstMarker)
  //   if (firstMarker?.position) {
  //     console.log('첫 번째 마커 위치로 이동:', firstMarker.position);

  //     viewer.animate({
  //       yaw: firstMarker.position.yaw,
  //       pitch: firstMarker.position.pitch,
  //       speed: 800,
  //     });
  //   }
  //   if (firstMarker && firstMarker.coords) {
  //     viewer.setPanorama(fileUrl, {
  //       position: {
  //         yaw: firstMarker.coords.yaw,
  //         pitch: firstMarker.coords.pitch
  //       }
  //     });
      
  //     // 또는 애니메이션으로 이동
  //     // viewer.animate({
  //     //   yaw: firstMarker.position.yaw,
  //     //   pitch: firstMarker.position.pitch,
  //     //   zoom: 50,  // 적당한 줌 값
  //     //   speed: '2rpm' 
  //     // });
  //   }
  // }, [markers, fileUrl]);

  return (
      <>
        {/* <Button variant="contained" onClick={handleOpen} style={{minWidth:"unset",color:"#fff", marginRight:"4px", height: "37px" ,padding:"3px 10px"}} color="success">
          <span className="icon_png"><img style={{width:"20px"}} src={`${process.env.PUBLIC_URL}/img/icon/view_in_ar.png`} alt='' /></span>&nbsp;VR
        </Button> */}
        <img
          src={fileUrl}
          alt="Thumbnail"
          // className="w-32 h-32 object-cover cursor-pointer rounded"
          onClick={handleOpen} 
          style={{width: "80px" ,height: "80px" ,padding:"3px", cursor:'pointer'}}
        />
  
        <Modal className="pop_box" open={open} onClose={handleClose} aria-labelledby="modal-modal-title" aria-describedby="modal-modal-description">
          <MainCard style={{maxWidth:"1440px", width:"90%", height:"75vh"}} title="" modal darkTitle content={false}>
            <CardContent style={{height:"100%"}}>
              <div className='pop_close'>
                <Button color="primary" size="small" onClick={handleClose}>
                  {t('message.common.btn.close')}
                </Button>
              </div>
  
              <div style={{width:"100%", height: '73vh'}}>
                <div ref={containerRef} style={{ width: '100%', height: '93%' }} />
              </div>
            </CardContent>
  
          </MainCard>
        </Modal>

      </>
  )
}

export default CompanyPanoViewerLayer;
  